import { Reducer } from 'redux';
import actionTypes from './asset-library-home.types';
import type {
  AssetLibraryHomeState,
  Channel,
  QuickLink,
} from '../types';

export type AssetLibraryHomeAction =
  | { type: typeof actionTypes.SET_LOADING; payload: boolean }
  | { type: typeof actionTypes.SET_ERROR; payload: string | null }
  | { type: typeof actionTypes.FETCH_HOME_SUCCESS; payload: { all: QuickLink[]; channels: Channel[] } }
  | { type: typeof actionTypes.CREATE_CHANNEL_SUCCESS; payload: Channel }
  | { type: typeof actionTypes.UPDATE_CHANNEL_SUCCESS; payload: Channel }
  | { type: typeof actionTypes.DELETE_CHANNEL_SUCCESS; payload: { channelId: string } }
  | { type: typeof actionTypes.ADD_QUICK_LINK_SUCCESS; payload: { channelId: string; quickLink: QuickLink } }
  | { type: typeof actionTypes.UPDATE_QUICK_LINK_SUCCESS; payload: { channelId: string; quickLink: QuickLink } }
  | { type: typeof actionTypes.DELETE_QUICK_LINK_SUCCESS; payload: { channelId: string; quickLinkId: string } }
  | { type: typeof actionTypes.DUPLICATE_QUICK_LINK_SUCCESS; payload: { channelId: string; quickLink: QuickLink } }
  | { type: typeof actionTypes.ARCHIVE_QUICK_LINK_SUCCESS; payload: { channelId: string; quickLinkId: string } }
  | { type: typeof actionTypes.OPEN_PANEL; payload: { channelId: string | null; quickLinkId: string | null } }
  | { type: typeof actionTypes.CLOSE_PANEL };

const initialState: AssetLibraryHomeState = {
  all: [],
  channels: [],
  loading: false,
  error: null,
  activePanelChannelId: null,
  activePanelQuickLinkId: null,
  isPanelOpen: false,
};

/**
 * Asset Library Home reducer — manages channels, flat quick-link list, and side-panel state
 * @param {AssetLibraryHomeState} state - Current state
 * @param {AssetLibraryHomeAction} action - Dispatched action
 * @returns {AssetLibraryHomeState} - Next state
 */
const reducer: Reducer<AssetLibraryHomeState, AssetLibraryHomeAction> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case actionTypes.FETCH_HOME_SUCCESS:
      return {
        ...state,
        all: action.payload.all,
        channels: action.payload.channels,
        loading: false,
        error: null,
      };

    case actionTypes.CREATE_CHANNEL_SUCCESS:
      return {
        ...state,
        channels: [
          ...state.channels,
          { ...action.payload, quickLinks: action.payload.quickLinks || [] },
        ],
      };

    case actionTypes.UPDATE_CHANNEL_SUCCESS:
      return {
        ...state,
        channels: state.channels.map((c) =>
          c._id === action.payload._id
            ? { ...c, ...action.payload, quickLinks: c.quickLinks }
            : c
        ),
      };

    case actionTypes.DELETE_CHANNEL_SUCCESS: {
      const { channelId } = action.payload;
      return {
        ...state,
        channels: state.channels.filter((c) => c._id !== channelId),
        all: state.all.filter((ql) => ql.channelId !== channelId),
      };
    }

    case actionTypes.ADD_QUICK_LINK_SUCCESS: {
      const { channelId, quickLink } = action.payload;
      return {
        ...state,
        all: [...state.all, quickLink],
        channels: state.channels.map((c) =>
          c._id === channelId
            ? { ...c, quickLinks: [...c.quickLinks, quickLink] }
            : c
        ),
      };
    }

    case actionTypes.UPDATE_QUICK_LINK_SUCCESS: {
      const { channelId, quickLink } = action.payload;
      return {
        ...state,
        all: state.all.map((ql) => (ql._id === quickLink._id ? quickLink : ql)),
        channels: state.channels.map((c) =>
          c._id === channelId
            ? {
                ...c,
                quickLinks: c.quickLinks.map((ql) =>
                  ql._id === quickLink._id ? quickLink : ql
                ),
              }
            : c
        ),
      };
    }

    case actionTypes.DELETE_QUICK_LINK_SUCCESS: {
      const { channelId, quickLinkId } = action.payload;
      return {
        ...state,
        all: state.all.filter((ql) => ql._id !== quickLinkId),
        channels: state.channels.map((c) =>
          c._id === channelId
            ? { ...c, quickLinks: c.quickLinks.filter((ql) => ql._id !== quickLinkId) }
            : c
        ),
      };
    }

    case actionTypes.DUPLICATE_QUICK_LINK_SUCCESS: {
      const { channelId, quickLink } = action.payload;
      return {
        ...state,
        all: [...state.all, quickLink],
        channels: state.channels.map((c) =>
          c._id === channelId
            ? { ...c, quickLinks: [...c.quickLinks, quickLink] }
            : c
        ),
      };
    }

    case actionTypes.ARCHIVE_QUICK_LINK_SUCCESS: {
      const { channelId, quickLinkId } = action.payload;
      return {
        ...state,
        all: state.all.filter((ql) => ql._id !== quickLinkId),
        channels: state.channels.map((c) =>
          c._id === channelId
            ? { ...c, quickLinks: c.quickLinks.filter((ql) => ql._id !== quickLinkId) }
            : c
        ),
      };
    }

    case actionTypes.OPEN_PANEL:
      return {
        ...state,
        isPanelOpen: true,
        activePanelChannelId: action.payload.channelId,
        activePanelQuickLinkId: action.payload.quickLinkId,
      };

    case actionTypes.CLOSE_PANEL:
      return {
        ...state,
        isPanelOpen: false,
        activePanelChannelId: null,
        activePanelQuickLinkId: null,
      };

    default:
      return state;
  }
};

export default reducer;
